import { router } from 'expo-router';
import { Copy, QrCode, Share2 } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { HeroWatermark } from '@/components/HeroWatermark';
import { HowItWorksSteps, type HowItWorksStep } from '@/components/HowItWorksSteps';
import { Overline } from '@/components/Overline';
import { ReferralDetailSheet } from '@/components/ReferralDetailSheet';
import { QrSheet } from '@/components/QrSheet';
import { Toast } from '@/components/Toast';
import {
  WhoClickedCard,
  DEFAULT_FILTERS,
  type WhoClickedFilters,
  type WhoClickedTextableStage,
} from '@/components/WhoClickedCard';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { useShareLink, type ShareProgram } from '@/hooks/useShareLink';
import { buildInviteMessage } from '@/lib/shareLinks';
import { referralLinksStore } from '@/stores/referralLinks.store';
import { everyoneFeedStore } from '@/stores/everyoneFeed.store';
import { referralStatsStore } from '@/stores/referralStats.store';
import type { EveryoneFeedItem } from '@/types/referral';

/**
 * App-invite screen — the tap-in target from the Refer tab index's invite
 * card. Shares the user's single generic ReferralLink (no deal): when the
 * invited friend completes their FIRST deal, any deal, their referrer is
 * paid, stacked on top of whatever that deal's own referrer cut pays.
 *
 * Every string comes from the server's `invite_program.marketing_copy`,
 * which is the same schema a Deal publishes — so a deal program and this
 * one render through identical components, and adding another program
 * means producing that hash, not touching the app.
 *
 * Sharing is direct (Share / Copy / QR) rather than via ShareMenu: the
 * invite is one stable link with no per-channel tracking, so the
 * channel-mint machinery doesn't apply.
 */
const InviteScreen = observer(() => {
  const { toast, qr, sharingId, share, copy, showQr, closeQr } = useShareLink();
  const [filters, setFilters] = useState<WhoClickedFilters>(DEFAULT_FILTERS);
  const [detailItem, setDetailItem] = useState<EveryoneFeedItem | null>(null);

  const onTextAll = useCallback(
    (stage: WhoClickedTextableStage) => router.push({ pathname: '/referrals/nudge', params: { stage } }),
    [],
  );
  const onRowNudge = useCallback((item: EveryoneFeedItem) => {
    setDetailItem(null);
    router.push({
      pathname: '/referrals/nudge',
      params: { preselect_referral_id: String(item.referral_id) },
    });
  }, []);

  // Stats carry the invite terms this screen renders — and whether the
  // program exists at all. The route is registered unconditionally, so a
  // deep link (or a bundle ahead of its backend) can land here with no
  // program published; nothing below may assume one.
  useEffect(() => {
    void referralStatsStore.ensureLoaded();
  }, []);
  const inviteProgram = referralStatsStore.data?.invite_program ?? null;

  // Mint-or-find eagerly so the first Share tap is instant — only once the
  // server says there is a program to share.
  useEffect(() => {
    if (!inviteProgram) return;
    void referralLinksStore.ensureGenericLink().catch(() => {
      // createError surfaces on the store; the buttons retry on tap.
    });
  }, [inviteProgram]);

  // Unscoped on purpose: the invite program IS account-level, so "everyone
  // who came through your links" is exactly its audience. The deal screen
  // passes a dealId to scope its own copy of this feed.
  useEffect(() => {
    const handle = setTimeout(() => {
      void everyoneFeedStore.fetch({
        stage: filters.stage,
        search: filters.search ?? null,
        sort: 'last_update',
      });
    }, filters.search ? 250 : 0);
    return () => clearTimeout(handle);
  }, [filters.stage, filters.search]);

  const totals = referralStatsStore.data?.totals;
  const friendStats = totals
    ? {
      inProgressCount: totals.friends_in_progress_count,
      leftOnTableCents: totals.left_on_table_cents,
      earnedCents: totals.earnings_cents,
    }
    : null;
  // Every string on this screen is the server's — same as a deal renders
  // its own marketing_copy. Nothing here is authored client-side.
  const inviteCopy = inviteProgram?.marketing_copy;
  const steps: readonly HowItWorksStep[] = inviteCopy?.how_it_works ?? [];

  const program: ShareProgram = useMemo(
    () => ({
      id: 0,
      resolveUrl: async () => (await referralLinksStore.ensureGenericLink()).public_url,
      message: buildInviteMessage(),
      qrCaption: 'Your MarketPryce invite link',
    }),
    [],
  );

  const onShare = useCallback(() => void share(program), [program, share]);
  const onCopy = useCallback(() => void copy(program), [copy, program]);
  const onShowQr = useCallback(() => void showQr(program), [program, showQr]);

  if (!inviteProgram) {
    return (
      <SafeAreaView style={styles.safe} edges={['top']}>
        <View style={styles.header}>
          <BackChevron />
        </View>
        <View style={styles.emptyContainer}>
          {referralStatsStore.loading && !referralStatsStore.data ? (
            <ActivityIndicator color={colors.deepGreen} />
          ) : (
            <EmptyState
              title="Invites aren't open yet"
              body="You can still earn by sharing any deal on the Refer tab."
              cta={{ label: 'Back to Refer', onPress: () => router.back() }}
              testID="invite-unavailable"
            />
          )}
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.header}>
          <BackChevron />
          <Text style={styles.headerTitle}>Invite a teammate</Text>
        </View>

        <Card variant="dark" style={styles.hero} testID="invite-hero">
          <HeroWatermark />
          <Overline tone="onDark">{inviteCopy?.eyebrow ?? 'Invite a teammate'}</Overline>
          <Text style={styles.heroAmount}>{inviteCopy?.hero_subhead ?? ''}</Text>
          <Text style={styles.heroBody}>{(inviteCopy?.hero_body_lines ?? []).join(' ')}</Text>
          <Button
            variant="primary"
            size="base"
            fullWidth
            label="Share my invite link"
            onPress={onShare}
            loading={sharingId !== null}
            leadingIcon={Share2}
            testID="invite-share"
          />
          <View style={styles.heroRow}>
            <Button
              variant="outlineOnDark"
              size="base"
              label="Copy link"
              onPress={onCopy}
              leadingIcon={Copy}
              style={styles.heroRowButton}
              testID="invite-copy"
            />
            <Button
              variant="outlineOnDark"
              size="base"
              label="QR code"
              onPress={onShowQr}
              leadingIcon={QrCode}
              style={styles.heroRowButton}
              testID="invite-qr"
            />
          </View>
        </Card>

        <WhoClickedCard
          items={everyoneFeedStore.items}
          chipCounts={everyoneFeedStore.chipCounts}
          filters={filters}
          onFiltersChange={setFilters}
          stats={friendStats}
          programName="a deal"
          onTextAll={onTextAll}
          onNudge={onRowNudge}
          onOpenDetail={setDetailItem}
          onUndoRemove={() => {}}
          onRequestRemove={() => {}}
          tombstonedIds={everyoneFeedStore.tombstoned}
          loading={everyoneFeedStore.refreshing && !everyoneFeedStore.loaded}
          error={everyoneFeedStore.loaded ? null : everyoneFeedStore.refreshError}
          onRetry={() =>
            void everyoneFeedStore.fetch({
              stage: filters.stage,
              search: filters.search ?? null,
              sort: 'last_update',
            })
          }
          title="Who you've invited"
          removable={false}
        />

        <HowItWorksSteps steps={steps} title="How it works" testID="invite-steps" />

        {inviteCopy?.compliance_disclosure ? (
          <Text style={styles.finePrint}>{inviteCopy.compliance_disclosure}</Text>
        ) : null}
      </ScrollView>

      <QrSheet
        isOpen={qr !== null}
        onClose={closeQr}
        url={qr?.url ?? null}
        caption={qr?.caption ?? 'Share link'}
      />
      {/* Read-only here (no onRemove): removal is a deal-screen affordance.
          The invite feed is every friend, and a row hidden here would hide
          everywhere. */}
      <ReferralDetailSheet
        isOpen={detailItem !== null}
        onClose={() => setDetailItem(null)}
        item={detailItem}
        onNudge={onRowNudge}
        testID="invite-detail-sheet"
      />

      <Toast message={toast} />
    </SafeAreaView>
  );
});

export default InviteScreen;

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  scroll: {
    padding: spacing.lg,
    gap: spacing.md,
    paddingBottom: spacing['3xl'],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  headerTitle: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.lg,
    color: colors.deepGreen,
  },
  hero: {
    padding: spacing.lg,
    // 28px hero override (DESIGN.md), not radii.lg.
    borderRadius: 28,
    overflow: 'hidden',
    gap: spacing.sm,
  },
  heroAmount: {
    fontFamily: fonts.numeric,
    fontSize: fontSizes['3xl'],
    lineHeight: 38,
    color: colors.electricGreen,
  },
  heroBody: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.textOnDarkMuted,
    marginBottom: spacing.xs,
  },
  heroRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  heroRowButton: { flex: 1 },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  finePrint: {
    fontFamily: fonts.body,
    fontSize: fontSizes.xs,
    color: colors.textMuted,
    paddingHorizontal: spacing.xs,
  },
});
